import * as React from 'react';
import GenericHoc from 'components/common/generic-hoc';
import { HocOptions } from 'components/common/generic-hoc.types';
import { StylesProps, ThemeType } from 'theme/jss-types';
import { formFields } from 'components/create-consignment/create-modal.constants';
import { useTranslation } from 'react-i18next';
import {
    Col,
    Form,
    FormInstance,
    Radio, // add Radio import
} from 'antd';
import { commonStyleSheet } from 'library/common-styles';

const styles = (theme: ThemeType) => ({
    formItem: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'left',
        '& .ant-checkbox + span': {
            color: '#111111',
            fontSize: 14,
            fontWeight: 600,
        },
    },
    title: {
        color: '#333333',
        fontFamily: 'Open Sans',
        fontSize: 12,
        letterSpacing: 0,
        width: '100%',
        marginBottom: 4,
        marginLeft: 4,
    },
    referenceTitle: {
        color: '#333333',
        fontFamily: 'Open Sans',
        fontSize: 12,
        letterSpacing: 0,
        width: '100%',
        marginBottom: 4,
    },
    contentType: {
        ...commonStyleSheet(theme).flexRow,
        '& .ant-form-item': {
            marginBottom: 0,
        },
    },
    dimensions: {
        ...commonStyleSheet(theme).flexColumn,
        alignItems: 'left',
    },
    dimentionTitle: {
        color: '#333333',
        fontFamily: 'Open Sans',
        fontSize: 12,
        letterSpacing: 0,
    },
});

interface IProps extends StylesProps<ReturnType<typeof styles>>, ReturnType<typeof mapStateToProps> {
    form: FormInstance;
    isRenderedFromLayout: boolean | undefined;
    params: any;
}

const RiskField = (props: IProps) => {
    const { t } = useTranslation();
    const {
        classes,
        isCpdp,
        showRiskSurchargeType,
        form,
        pieceSubLayout,
        documentSubLayout,
        envelopeSubLayout,
        isRenderedFromLayout,
        params,
    } = props;
    const {
        RiskSurcharge,
        ItemType,
        DeclaredValue,
        ConsignmentCategory,
    } = formFields;

    const {
        key, pieceIndex, getValidationRules, disabledFields = {},
    } = params;
    const itemType = Form.useWatch(ItemType.key, form);
    const consignmentCategory = Form.useWatch(ConsignmentCategory.key, form);
    const declaredAmount = Form.useWatch(DeclaredValue.key, form);
    const isPieceLevel = pieceIndex !== null && pieceIndex !== undefined;

    const getDisableRiskSurcharge = () => {
        if (!isCpdp || consignmentCategory === 'international') return false;
        if (!showRiskSurchargeType) return false;
        if (declaredAmount > 200000) {
            return true;
        }
        if (declaredAmount > 100000) {
            return true;
        }
        if (declaredAmount > 50000) {
            return false;
        }
        return false;
    };

    const disableRiskSurcharge = getDisableRiskSurcharge();

    if (!isRenderedFromLayout) {
        if (itemType === 'document' && !documentSubLayout.includes('isRiskSurchargeApplicable')) {
            return <></>;
        }
        if (itemType === 'envelope' && !envelopeSubLayout.includes('isRiskSurchargeApplicable')) {
            return <></>;
        }
        if ((!itemType || itemType === 'non-document') && !pieceSubLayout.includes('isRiskSurchargeApplicable')) {
            return <></>;
        }
    }

    const riskSurchargeKey = isPieceLevel ? key : RiskSurcharge.key;

    return (
        <Col span={24} xs={24} sm={24} md={12}>
            <div className={classes.formItem}>
                <div className={classes.referenceTitle}>
                    {t(RiskSurcharge.key)}
                </div>
                <Form.Item
                    name={riskSurchargeKey}
                    className={classes.contentType}
                    initialValue={RiskSurcharge.defaultValue}
                    dependencies={[DeclaredValue.key]}
                    rules={getValidationRules(RiskSurcharge, isPieceLevel)}
                >
                    <Radio.Group disabled={disableRiskSurcharge || !!disabledFields[riskSurchargeKey || '']}>
                        <Radio value>{t('Yes')}</Radio>
                        <Radio value={false}>{t('No')}</Radio>
                    </Radio.Group>
                </Form.Item>
            </div>
        </Col>
    );
};

const mapStateToProps = (state: any) => {
    const { master } = state;
    const { config, Customer } = master;
    const customerPortalConfig = config?.customer_portal_config;
    const isCpdp = Customer?.is_cpdp;
    const showRiskSurchargeType = customerPortalConfig?.show_risk_surcharge_type;
    const formSubLayout = config?.customer_portal_order_creation_config?.sub_layout || {};
    const documentSubLayout = formSubLayout?.document || [];
    const pieceSubLayout = formSubLayout?.non_document || [];
    const envelopeSubLayout = formSubLayout?.envelope || [];
    return {
        isCpdp,
        showRiskSurchargeType,
        pieceSubLayout,
        documentSubLayout,
        envelopeSubLayout,
    };
};

const hocConfig: HocOptions = {
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
    connectJss: {
        useJss: true,
        styleSheet: styles,
    },
};

export default GenericHoc(hocConfig)(RiskField);
